import { Typography } from '@workspace/ui/components/Typography';
import { useBreakpoint } from '@workspace/ui/hooks/useBreakpoint';
import { cn } from '@workspace/ui/lib/utils';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { company } from '~/company/Company';
import { formatVolume } from '~/components/ProductCard/utils';
import {
  type ProductBadge,
  useProductCardV3,
} from '~/components/ProductCardV3/hooks/useProductCardV3';
import {
  AddToCartSection,
  ProductBadges,
  ProductImage,
  ProductInfo,
} from '~/components/ProductCardV3/shared';
import type { ProductCardVariantProps } from '~/components/ProductCardV3/types';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';

import styles from './ProductCardS.module.scss';

/**
 * ProductCardS - Small size product card (96px mobile, 164px web)
 * Compact card for recommendations and sliders
 */
const ProductCardS = ({
  product,
  className,
  hideButton = false,
  hideBadges = false,
  productState,
  disabled = false,
  transparent = false,
}: ProductCardVariantProps) => {
  const { t } = useTranslation();
  const { handleClickProduct } = useCategoryAnalytics();
  const { isMobile } = useBreakpoint();

  const {
    currentItem,
    limit,
    image,
    oldPrice,
    currentPrice,
    cartItemsCount,
    isFavorite,
    isOutOfStock,
    badges,
    multiBuyInfo,
    productHasManyVariants,
    isBundle,
    toggleFavorite,
    changeCount,
    openSelectSKUModal,
  } = useProductCardV3(
    productState?.data ?? product,
    productState?.selectedOffer,
    's',
    hideBadges,
  );

  const handleProductClick = useCallback(() => {
    handleClickProduct(product);
  }, [product, handleClickProduct]);

  const [suggestedBadge, otherBadges] = badges.reduce<
    [ProductBadge | null, ProductBadge[]]
  >(
    (acc, badge) => {
      if (badge.type === 'suggested') {
        acc[0] = badge;
      } else {
        if (company.is('mcosmetic')) {
          if (badge.type === 'discount') acc[1].push(badge);
        } else acc[1].push(badge);
      }
      return acc;
    },
    [null, []],
  );

  const visibleBadges = company.match({
    mcosmetic: otherBadges,
    default: suggestedBadge ? [suggestedBadge] : [],
  });

  const volume = formatVolume(currentItem.properties);
  const hasDiscount = oldPrice && oldPrice !== currentPrice;

  const PriceComponent = isMobile
    ? Typography.Text50Semibold
    : Typography.Text400Semibold;

  return (
    <div
      className={cn(
        styles.card,
        disabled && styles.disabled,
        transparent && 'bg-transparent!',
        className,
      )}
      data-company={company.name}
    >
      <div className={styles.imageWrapper}>
        <ProductImage
          src={image}
          alt={currentItem.name}
          size="s"
          badges={
            visibleBadges.length ? (
              <ProductBadges
                variant={company.match({
                  mcosmetic: transparent ? 'transparent' : 'brandOutline',
                  default: 'primary',
                })}
                badges={visibleBadges}
                size="s"
              />
            ) : null
          }
          isFavorite={isFavorite}
          onToggleFavorite={toggleFavorite}
          productSlug={product.slug}
          offerSku={currentItem.sku ?? ''}
          onProductClick={handleProductClick}
          cartItemsCount={cartItemsCount}
          isOutOfStock={isOutOfStock}
          transparent={transparent}
        />

        {!hideButton && (
          <AddToCartSection
            currentPrice={currentPrice}
            oldPrice={hasDiscount ? oldPrice : undefined}
            cartItemsCount={cartItemsCount}
            limit={limit}
            size={company.match({
              mcosmetic: 'xs',
              default: 's',
            })}
            onChangeCount={changeCount}
            className={styles.addToCartSection}
            hidePrice
            productId={productState?.data?.id ?? product?.id ?? 0}
            sku={productState?.selectedOffer?.sku ?? currentItem.sku ?? ''}
            isOutOfStock={isOutOfStock}
            productSlug={product.slug}
            productHasManyVariants={productHasManyVariants}
            isBundle={isBundle}
            onOpenSelectSKUModal={openSelectSKUModal}
            transparent={transparent}
          />
        )}
      </div>

      <div className={styles.infoWrapper}>
        <ProductInfo
          name={currentItem.name}
          volume={volume}
          rating={currentItem.ratingAverage}
          reviewsCount={currentItem.ratingMarksCount}
          isRatingShown={false}
          size="s"
          productSlug={product.slug}
          currentSku={currentItem.sku ?? ''}
          transparent={transparent}
        />

        {!isOutOfStock && (
          <div className={styles.priceWrapper}>
            <PriceComponent
              as="span"
              className={
                transparent ? 'text-text-inverse-primary' : 'text-text-primary'
              }
            >
              {currentPrice}
            </PriceComponent>
            {hasDiscount && (
              <span
                className={cn(
                  styles.oldPrice,
                  transparent && 'text-graphic-transparent-white-55!',
                )}
              >
                {oldPrice}
              </span>
            )}

            {multiBuyInfo && (
              <div className={styles.multiBuyInfo}>
                {cartItemsCount === 0
                  ? t('multibuyOfferNoCartValue', {
                      count: multiBuyInfo.remainingCount,
                      value: multiBuyInfo.discountValue,
                    })
                  : multiBuyInfo.remainingCount &&
                      multiBuyInfo.nextDiscountPercent
                    ? t('multibuyOfferWithCartValue', {
                        count: Math.max(0, multiBuyInfo.remainingCount),
                        value: multiBuyInfo.nextDiscountValue,
                      })
                    : null}
              </div>
            )}
          </div>
        )}
      </div>

      {disabled && <div className={styles.disabledOverlay} />}
    </div>
  );
};

export default observer(ProductCardS);
